import Result, { ok, error } from "../types/result";

type UnauthorizedHandler = (response: Response) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export const setUnauthorizedHandler = (fn: UnauthorizedHandler | null): void => {
  unauthorizedHandler = fn;
};

export const runUnauthorizedHandler = (response: Response): void => {
  unauthorizedHandler?.(response);
};

export async function parseResponse<T>(response: Response): Promise<Result<T>> {
  if (response.status === 401) {
    runUnauthorizedHandler(response);
  }

  const body = await response.json();

  if (response.ok) {
    return ok(body);
  } else if (typeof body?.message?.code === "string") {
    // Adora's HTTPException handler wraps `detail` as `{ message: <detail> }`.
    // A coded object must stay intact so the caller can branch on `code`, so
    // this is checked before `reason` - a coded payload that also carries a
    // `reason` must not be flattened into a message and lose its `code`.
    const payload = body.message as {
      code: string;
      message?: string;
      reason?: string;
    };
    return error({
      tag: "codedError",
      code: payload.code,
      message: payload.message ?? payload.reason ?? "Request failed",
      detail: payload,
      trace: body?.trace
    });
  } else if (body?.message?.reason && body?.message?.errors) {
    return error({
      tag: "validationError",
      message: body.message.reason,
      errors: body.message.errors,
      trace: body?.trace
    });
  } else if (body?.message?.reason) {
    return error({ tag: "traceError", message: body.message.reason, trace: body?.trace });
  } else if (body.message) {
    return error({ tag: "traceError", message: body.message, trace: body.trace });
  } else if (typeof body?.detail === "string") {
    return error({ tag: "traceError", message: body.detail, trace: body?.trace });
  } else if (body?.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
    // FastAPI HTTPException(detail={message, errors}) or detail={reason, errors}
    const detail = body.detail as { code?: string; message?: string; reason?: string; errors?: string[] };
    const message = detail.message ?? detail.reason ?? "Request failed";
    // A coded detail describes a specific condition the caller can act on,
    // so keep the payload intact rather than flattening it to a message.
    if (typeof detail.code === "string") {
      return error({ tag: "codedError", code: detail.code, message, detail, trace: body?.trace });
    }
    if (detail.errors?.length) {
      return error({
        tag: "validationError",
        message,
        errors: detail.errors,
        trace: body?.trace
      });
    }
    return error({ tag: "traceError", message, trace: body?.trace });
  } else if (body.detail && Array.isArray(body.detail)) {
    // pydantic error
    const { msg: message } = body.detail?.[0] || "";
    const badValue = body.detail?.[0]?.input?.[0] || "";
    return error({ tag: "valueError", message, value: badValue });
  } else {
    // TODO maybe throw here
    return error({ tag: "unknownError", message: "An unknown error occured" });
  }
}
